import { RefObject, useEffect, useRef, useCallback } from "react";

import { useLiveFormEditorContext } from "../common/LiveFormEditorContext";

import { useScrollPreviewWithArrow } from "./useScrollPreviewWithArrow";

import { useArrow } from "@/components/LiveEditorElements/AnimatePreview/Arrow/ArrowContext";

interface QuestionWithScope {
  scope: "buyer" | "participant";
  askForAllRates?: boolean;
  ratesAssigned?: { value: string }[];
}

const isQuestionAssignedToRate = (question: QuestionWithScope, rateId: string): boolean => {
  if (question.scope !== "participant") {
    return false;
  }
  if (question.askForAllRates) {
    return true;
  }
  return question.ratesAssigned?.some((r) => r.value === rateId) ?? false;
};

export const useAnimatePreviewOnQuestionsEventsV3 = <T extends QuestionWithScope>(
  formContainerRef: RefObject<HTMLElement | null>,
  questions: T[],
  rateIds: string[] = []
) => {
  const { currentStepIndex: currentStep, previewDevice, setPreviewStepKey } = useLiveFormEditorContext();
  const { hideArrow } = useArrow();
  const { scrollAndUpdateArrow } = useScrollPreviewWithArrow();
  const previousQuestionsLengthRef = useRef<number>(questions.length);
  const focusedQuestionIndexRef = useRef<number | undefined>(undefined);

  const getRateIndexForQuestion = useCallback(
    (question: T): number => {
      if (question.scope === "buyer" || question.askForAllRates) {
        return 0;
      }
      if (question.ratesAssigned && question.ratesAssigned.length > 0) {
        const firstRateId = question.ratesAssigned[0].value;
        const index = rateIds.indexOf(firstRateId);
        return index >= 0 ? index : 0;
      }
      return 0;
    },
    [rateIds]
  );

  const getDomElementIndex = useCallback(
    (globalIndex: number): number => {
      const question = questions[globalIndex];
      if (!question) {
        return 0;
      }

      if (question.scope === "buyer") {
        let buyerIndex = 0;
        for (let i = 0; i < globalIndex; i++) {
          if (questions[i].scope === "buyer") {
            buyerIndex++;
          }
        }
        return buyerIndex;
      }

      const rateIndex = getRateIndexForQuestion(question);
      const targetRateId = rateIds[rateIndex];
      if (!targetRateId) {
        return 0;
      }

      let answerIndex = 0;
      for (let i = 0; i < globalIndex; i++) {
        const q = questions[i];
        if (isQuestionAssignedToRate(q, targetRateId)) {
          answerIndex++;
        }
      }
      return answerIndex;
    },
    [questions, rateIds, getRateIndexForQuestion]
  );

  const handleQuestionFocus = useCallback(
    (globalIndex: number) => {
      const question = questions[globalIndex];
      if (!question) {
        return;
      }

      if (focusedQuestionIndexRef.current !== globalIndex) {
        hideArrow();
        focusedQuestionIndexRef.current = globalIndex;
      }

      setPreviewStepKey(question.scope === "buyer" ? "payment" : "selection");

      const domIndex = getDomElementIndex(globalIndex);
      const rateIndex = question.scope === "participant" ? getRateIndexForQuestion(question) : undefined;

      scrollAndUpdateArrow({
        questionIndexInFocus: domIndex,
        scope: question.scope,
        rateIndexInFocus: rateIndex,
      });
    },
    [questions, hideArrow, setPreviewStepKey, getDomElementIndex, getRateIndexForQuestion, scrollAndUpdateArrow]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (questions.length > 0) {
        handleQuestionFocus(0);
      }
    }, 700);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally only run on mount/step/device changes
  }, [currentStep, previewDevice]);

  useEffect(() => {
    if (questions.length !== previousQuestionsLengthRef.current) {
      const isAdding = questions.length > previousQuestionsLengthRef.current;
      const focusIndex = isAdding ? questions.length - 1 : 0;

      previousQuestionsLengthRef.current = questions.length;

      if (questions.length > 0 && questions[focusIndex]) {
        setTimeout(() => {
          handleQuestionFocus(focusIndex);
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally react to questions.length changes only
  }, [questions.length, handleQuestionFocus]);

  useEffect(() => {
    const formElement = formContainerRef.current;
    if (!formElement) {
      return;
    }

    const handleFocusIn = (event: FocusEvent) => {
      let questionIndexInFocus: number | undefined;
      let element = event.target as HTMLElement | null;

      while (element) {
        if (element.hasAttribute("data-question-index")) {
          const index = element.getAttribute("data-question-index");
          questionIndexInFocus = index ? parseInt(index, 10) : undefined;
          break;
        }
        element = element.parentElement;
      }

      if (questionIndexInFocus === undefined) {
        return;
      }

      handleQuestionFocus(questionIndexInFocus);
    };

    formElement.addEventListener("focusin", handleFocusIn);
    return () => {
      formElement.removeEventListener("focusin", handleFocusIn);
    };
  }, [formContainerRef, handleQuestionFocus]);

  useEffect(() => {
    scrollAndUpdateArrow({});
  }, [previewDevice, scrollAndUpdateArrow]);
};
